import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  DownloadSimple,
  FilePdf,
  FileDoc,
  EnvelopeSimple,
  Warning,
  SpinnerGap,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 12;
const POLL_INTERVAL = 2500;

const PurchaseSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [status, setStatus] = useState('checking'); // checking | success | failed | expired
  const [bundle, setBundle] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error('status check failed');
        const data = await res.json();

        if (data.payment_status === 'paid' && data.download_token) {
          setStatus('success');
          const bRes = await fetch(`${API_URL}/api/download/${data.download_token}`);
          if (bRes.ok) setBundle({ ...(await bRes.json()), token: data.download_token });
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        pollCount.current += 1;
        if (pollCount.current < MAX_POLLS) {
          setTimeout(poll, POLL_INTERVAL);
        } else {
          setStatus('failed');
        }
      } catch (e) {
        pollCount.current += 1;
        if (pollCount.current < MAX_POLLS) setTimeout(poll, POLL_INTERVAL);
        else setStatus('failed');
      }
    };
    poll();
  }, [sessionId]);

  const handleDownload = (file) => {
    // Trigger authenticated download via token
    const url = `${API_URL}${file.download_url}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <main data-testid="purchase-success-page" className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]" dir="rtl">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {status === 'checking' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-8 text-center"
          >
            <SpinnerGap size={48} className="animate-spin text-[#1F3D2B] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic']">
              جاري التحقق من الدفع...
            </h2>
            <p className="text-[#6B7565]">
              نتحقق من نجاح الدفع وإعداد نسختك المخصّصة. لحظات قليلة.
            </p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
            <div className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-[#4A641F] flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} weight="fill" className="text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
                تم الشراء بنجاح!
              </h1>
              <p className="text-[#6B7565] leading-8">
                شكراً لك. نسختك مختومة باسم منشأتك وجاهزة للتحميل الآن.
              </p>
              {bundle && (
                <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#4A641F] bg-[#4A641F]/10 px-4 py-2 rounded-full">
                  <EnvelopeSimple size={16} />
                  <span>تم إرسال رابط التحميل إلى بريدك الإلكتروني أيضاً</span>
                </div>
              )}
            </div>

            {bundle && (
              <div data-testid="download-bundle" className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6">
                <h2 className="text-lg font-bold text-[#1F3D2B] mb-1 font-['IBM_Plex_Sans_Arabic']">
                  {bundle.product_name}
                </h2>
                <p className="text-sm text-[#7A866E] mb-2">
                  مرحباً {bundle.customer_name} — نسخة خاصة بـ:{' '}
                  <span className="text-[#4A641F] font-medium">{bundle.organization_name}</span>
                </p>
                <p className="text-xs text-[#92A082] mb-5">{bundle.expires_message}</p>

                <div className="space-y-3">
                  {bundle.files?.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-[#F5F2EC] rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        {file.kind === 'pdf' ? (
                          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <FilePdf size={24} className="text-red-500" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileDoc size={24} className="text-blue-500" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#1F3D2B]">{file.label}</p>
                          <p className="text-xs text-[#7A866E]">{file.filename}</p>
                        </div>
                      </div>
                      <Button
                        data-testid={`download-file-${file.kind}`}
                        onClick={() => handleDownload(file)}
                        className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-4 py-2 flex items-center gap-2"
                      >
                        <DownloadSimple size={18} />
                        تحميل
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center">
              <Link to="/store">
                <Button variant="outline" className="border-[#D8D2C8] text-[#1F3D2B] rounded-xl px-6 py-3">
                  العودة للمتجر
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {(status === 'failed' || status === 'expired') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <Warning size={40} className="text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              {status === 'expired' ? 'انتهت صلاحية الجلسة' : 'لم نتمكن من تأكيد الدفع'}
            </h2>
            <p className="text-[#6B7565] mb-6">
              {status === 'expired'
                ? 'يرجى المحاولة مرة أخرى من المتجر.'
                : 'إذا تم خصم المبلغ سيصلك بريد التأكيد خلال دقائق. للمساعدة تواصل معنا.'}
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/store">
                <Button className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-6 py-3">
                  العودة للمتجر
                </Button>
              </Link>
              <Link to="/booking">
                <Button variant="outline" className="border-[#D8D2C8] text-[#1F3D2B] rounded-xl px-6 py-3">
                  تواصل معنا
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default PurchaseSuccessPage;
